/**
 * PAYSTUB WIZARD — PIN PROTECTION
 * On-screen numeric keypad (no OS keyboard required)
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Lock, Shield, Eye, EyeOff, Delete } from 'lucide-react';
import { verifyOwnerPin } from './PinLock';


export default function PinProtection({ onAuthenticated }) {
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const [lockTimer, setLockTimer] = useState(0);
  const [shake, setShake] = useState(false);

  // Lockout countdown
  useEffect(() => {
    if (locked && lockTimer > 0) {
      const t = setTimeout(() => setLockTimer((s) => s - 1), 1000);
      return () => clearTimeout(t);
    }
    if (lockTimer === 0 && locked) {
      setLocked(false);
      setAttempts(0);
    }
  }, [locked, lockTimer]);

  // Auto-verify when PIN is 4 digits
  useEffect(() => {
    if (pin.length === 4) {
      const id = setTimeout(() => verifyPin(pin), 150);
      return () => clearTimeout(id);
    }
  }, [pin]); // eslint-disable-line

  const pressKey = (digit) => {
    if (locked) return;
    if (pin.length >= 4) return;
    setPin((p) => p + digit);
  };

  const pressBackspace = () => {
    if (locked) return;
    setPin((p) => p.slice(0, -1));
  };

  const verifyPin = async (enteredPin) => {
    try {
      await verifyOwnerPin(enteredPin);
      sessionStorage.setItem('paystub_authenticated', 'true');
      onAuthenticated();
      return;
    } catch (e) {
      if (e.response?.status === 429) { setPin(''); setLocked(true); setLockTimer(60); toast.error(e.response.data.detail); return; }
    }
    const next = attempts + 1;
    setAttempts(next);
    setShake(true);
    setTimeout(() => setShake(false), 500);
    if (next >= 3) {
      setLocked(true);
      setLockTimer(30);
      toast.error('Too many attempts. Locked for 30 seconds.');
    } else {
      toast.error(`Invalid PIN. ${3 - next} attempt${3 - next === 1 ? '' : 's'} remaining.`);
    }
    setPin('');
  };

  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <Card className={`w-full max-w-sm bg-zinc-900 border-zinc-700 ${shake ? 'animate-shake' : ''}`}>
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-3 p-3 bg-amber-500/20 rounded-full w-fit">
            <Lock className="w-8 h-8 text-amber-400" />
          </div>
          <CardTitle className="text-2xl text-white">Enter Access PIN</CardTitle>
          <p className="text-sm text-zinc-400 mt-1">Tap the keypad below</p>
        </CardHeader>

        <CardContent className="space-y-5">
          {/* PIN dots/digits display */}
          <div className="flex justify-center gap-3" data-testid="pin-display">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`w-14 h-16 rounded-lg border-2 flex items-center justify-center text-3xl font-bold transition-all ${
                  pin.length === i && !locked
                    ? 'border-amber-500 bg-zinc-800'
                    : pin.length > i
                    ? 'border-amber-500/60 bg-amber-500/10 text-amber-300'
                    : 'border-zinc-700 bg-zinc-800'
                }`}
              >
                {pin[i] ? (showPin ? pin[i] : '●') : ''}
              </div>
            ))}
          </div>

          {/* Show/hide toggle */}
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPin(!showPin)}
              className="text-zinc-400 hover:text-white"
              data-testid="toggle-pin-visibility"
            >
              {showPin ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              {showPin ? 'Hide' : 'Show'}
            </Button>
          </div>

          {/* On-screen numeric keypad */}
          <div className="grid grid-cols-3 gap-3 select-none">
            {keys.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => pressKey(k)}
                disabled={locked || pin.length >= 4}
                className="h-16 rounded-xl bg-zinc-800 hover:bg-zinc-700 active:bg-amber-600 active:scale-95 border border-zinc-700 text-white text-3xl font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation"
                data-testid={`keypad-${k}`}
              >
                {k}
              </button>
            ))}
            {/* Bottom row: blank, 0, backspace */}
            <div />
            <button
              type="button"
              onClick={() => pressKey('0')}
              disabled={locked || pin.length >= 4}
              className="h-16 rounded-xl bg-zinc-800 hover:bg-zinc-700 active:bg-amber-600 active:scale-95 border border-zinc-700 text-white text-3xl font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation"
              data-testid="keypad-0"
            >
              0
            </button>
            <button
              type="button"
              onClick={pressBackspace}
              disabled={locked || pin.length === 0}
              className="h-16 rounded-xl bg-zinc-800 hover:bg-zinc-700 active:bg-red-600 active:scale-95 border border-zinc-700 text-white flex items-center justify-center transition-all disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation"
              aria-label="Backspace"
              data-testid="keypad-backspace"
            >
              <Delete className="w-7 h-7" />
            </button>
          </div>

          {locked && (
            <div className="text-center text-red-400 text-sm font-medium">
              Locked. Try again in {lockTimer}s
            </div>
          )}

          <Button
            onClick={() => verifyPin(pin)}
            disabled={locked || pin.length !== 4}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white h-12 text-base"
            data-testid="unlock-btn"
          >
            <Shield className="w-4 h-4 mr-2" />
            Unlock
          </Button>
        </CardContent>
      </Card>

      {/* shake keyframe */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
        .animate-shake { animation: shake 0.4s ease-in-out; }
      `}</style>
    </div>
  );
}
